import React from "react";
import clsx from "clsx";

import { IClass, ISchoolManager, IStudent } from "../../../state";
import { EStudentSortType, SortStudents } from "../../../libs/Util";
import { API_URL } from "../../../managerStore";
import { ModalContext } from "../../../libs/ModalProvider";
import useCurrentYear from "../../../hooks/manager/useCurrentYear";
import useSchoolStatus from "../../../hooks/manager/useSchoolStatus";
import { FileDownloader } from "../../../molecules/FileDownloader";
import Confirm from "../../../molecules/Confirm";
import { ReactComponent as ErrorIcon } from "../../../assets/svg/error.svg";

import style from "./StudentList.module.scss";

const pdfType = {
  TEACHER: "先生閲覧用",
  STUDENT: "生徒配布用",
} as const;

type PdfType = typeof pdfType[keyof typeof pdfType];

export interface IStudentListDispatchProps {
  loginCalling: boolean;
  resetPasswordCalling: boolean;
  passwordListCalling?: boolean;
  dlpLogin: (id: number) => void;
  resetPassword: (id: number) => void;
  toBuildStudent: (id: number) => void;
  toBuildNewStudent: () => void;
  toBuildBulkNewStudents: () => void;
  toBulkEditStudents: () => void;
  fetchTemporaryToken?: () => void;
  clearTemporaryToken?: () => void;
  onSaveDisableStudentPlans: (id: number, end_at: string) => void;
  isMasqueradeByAdmin?: boolean;
}

export interface IStudentListStateProps {
  temporary_token?: string;
  gearLogin?: (id: number) => void;
  students: IStudent[];
  classes: IClass[];
  next_classes?: IClass[];
  school: ISchoolManager;
  isNextYear: boolean;
}

export interface IStudentListProps
  extends IStudentListDispatchProps,
    IStudentListStateProps {
  title: string;
}

const CapacityRow = (props: {
  label: string;
  limitsByGrade?: Record<string, number>;
  countsByGrade: Record<number, { dlp: number; g4s: number }>;
  countKey: "dlp" | "g4s";
  totalCount: number;
  totalLimit: number;
  testAccountNum: number;
  maxGrade: number;
}) => (
  <div className={style.capacityRow}>
    <span className={style.capacityLabel}>{props.label}</span>
    <div className={style.capacityGrades}>
      {Array.from({ length: props.maxGrade }, (_, i) => i + 1).map(grade => {
        const limit = props.limitsByGrade?.[String(grade)] ?? 0;
        const count = props.countsByGrade[grade]?.[props.countKey] ?? 0;
        return (
          <div
            key={grade}
            className={clsx(
              style.capacityCell,
              props.totalCount >= props.totalLimit &&
                count > 0 &&
                count >= limit &&
                style.capacityCellExceeded,
            )}
          >
            <div className={style.capacityGradeLabel}>{grade}年</div>
            <div className={style.capacityGradeValue}>
              {count} / {limit}
            </div>
          </div>
        );
      })}
      <div className={style.capacityCellReserve}>
        <div className={style.capacityGradeLabel}>テスト枠</div>
        <div className={style.capacityGradeValue}>{props.testAccountNum}</div>
      </div>
      <div
        className={clsx(
          style.capacityCellTotal,
          props.totalCount >= props.totalLimit && style.capacityCellExceeded,
        )}
      >
        <div className={style.capacityGradeLabel}>合計</div>
        <div className={style.capacityGradeValue}>
          {props.totalCount} / {props.totalLimit}
        </div>
      </div>
    </div>
  </div>
);

export const StudentList = (props: IStudentListProps) => {
  const ctx = React.useContext(ModalContext);
  const { currentYearShortLabel, nextYearShortLabel } = useCurrentYear();
  const { isCramSchool } = useSchoolStatus();
  const clearTemporaryToken = props.clearTemporaryToken;
  const [searchName, setSearchName] = React.useState("");
  const [searchClass, setSearchClass] = React.useState<number | "">("");
  const [sortBy, setSortBy] = React.useState<EStudentSortType>(
    EStudentSortType.id,
  );
  const [tempPwdType, setTempPwdType] = React.useState<PdfType>();
  const students = React.useMemo(() => {
    let students = props.students;
    if (searchName) {
      students = students.filter(s => {
        const fullName = `${s.last_name} ${s.first_name}`;
        return fullName.match(searchName);
      });
    }
    if (searchClass) {
      students = students.filter(s => {
        return s.class_id === searchClass || s.next_klass_id === searchClass;
      });
    }
    SortStudents(students, sortBy);

    return [...students];
  }, [props.students, searchName, searchClass, sortBy]);

  const searchClassName = React.useMemo(() => {
    if (searchClass) {
      return props.classes.find(c => c.id === searchClass)?.full_name;
    }
    return "すべてのクラス";
  }, [searchClass, props.classes]);

  const hasStudentWithoutClass =
    students.filter(st => st.class_id === null).length > 0;

  const maxGrade = props.school.max_grade ?? 3;

  const studentCounts = React.useMemo(() => {
    const byGrade: Record<number, { dlp: number; g4s: number }> = {};
    for (let g = 1; g <= maxGrade; g++) {
      byGrade[g] = { dlp: 0, g4s: 0 };
    }
    let totalDlp = 0;
    let totalG4s = 0;
    props.students.forEach(s => {
      if (s.dlp_enabled) totalDlp++;
      if (s.g4s_enabled) totalG4s++;
      if (s.current_grade >= 1 && s.current_grade <= maxGrade) {
        if (s.dlp_enabled) byGrade[s.current_grade].dlp++;
        if (s.g4s_enabled) byGrade[s.current_grade].g4s++;
      }
    });
    return { byGrade, totalDlp, totalG4s };
  }, [props.students, maxGrade]);

  const handleDownloadButtonClick = React.useCallback(
    (pdfType: PdfType) => {
      if (hasStudentWithoutClass) {
        ctx.actions.showModal(
          <Confirm
            content={
              <div>
                <p>
                  クラス未設定の生徒がいます。生徒のクラス未設定の場合、デジタル教材へのログインができません。すべての生徒にクラスを設定してください。
                </p>
                <p>
                  ダウンロードを続ける場合には「OK」ボタンを押してください。
                </p>
              </div>
            }
            callback={() => {
              setTempPwdType(pdfType);
              props.fetchTemporaryToken && props.fetchTemporaryToken();
            }}
            closeModal={() => ctx.actions.closeModal()}
          />,
        );
      } else {
        setTempPwdType(pdfType);
        props.fetchTemporaryToken && props.fetchTemporaryToken();
      }
    },
    [hasStudentWithoutClass, ctx.actions, props],
  );

  React.useEffect(() => {
    if (props.temporary_token && tempPwdType) {
      ctx.actions.showModal(
        <FileDownloader
          closeModal={() => {
            ctx.actions.closeModal();
          }}
          content={
            <span>
              仮パスワードダウンロード({tempPwdType})を
              <br />
              ダウンロードしますか？
              <br />※{searchClassName}
            </span>
          }
          url={`${API_URL}/students/${
            tempPwdType === pdfType.TEACHER
              ? "list"
              : "export_temporary_password"
          }.pdf?temporary_token=${props.temporary_token}&class_id=${
            searchClass || ""
          }`}
        />,
      );
      clearTemporaryToken && clearTemporaryToken();
    }
  }, [
    ctx.actions,
    props.temporary_token,
    clearTemporaryToken,
    tempPwdType,
    searchClass,
    searchClassName,
  ]);

  return (
    <div className={style.list}>
      <div className="d-flex align-items-center mb-4">
        <h3 className="me-4 mb-0">{props.title}</h3>
        <p className="d-flex flex-column mb-0">
          <span>
            登録したクラスに所属する生徒を登録/編集することができます。
            <br />
            生徒を登録すると生徒用アカウントが発行されるので、仮パスワードを入力のうえログインしてください。
            {!props.isNextYear && (
              <>
                <br />
                ログインする際は、「仮パスワードダウンロード({pdfType.TEACHER}
                )」または「仮パスワードダウンロード({pdfType.STUDENT})」より
                <br />
                生徒のパスワードをダウンロードし、生徒に共有してください。
                <br />
                ※初回授業前にログインをお済ませ下さい。
              </>
            )}
          </span>
          <br />
          <span className={style.underText}>
            生徒のGmailアドレス、またはMicrosoftのアドレスをご登録いただいた場合、
            <br />
            ログインID/パスワードを入力しなくてもログインが可能です。
          </span>
        </p>
      </div>
      <div className={style.actionArea}>
        <div>
          <button
            className="btn btn-outline-primary me-3"
            onClick={e => {
              e.preventDefault();
              props.toBuildNewStudent();
            }}
          >
            新規登録
          </button>
          <button
            className="btn btn-outline-primary me-3"
            onClick={e => {
              e.preventDefault();
              props.toBuildBulkNewStudents();
            }}
          >
            新規一括登録
          </button>
          <button
            className="btn btn-outline-primary me-3"
            onClick={e => {
              e.preventDefault();
              props.toBulkEditStudents();
            }}
          >
            一括編集
          </button>
        </div>
        <div className={style.rightAlignedButtons}>
          {!props.isNextYear && props.fetchTemporaryToken && (
            <div>
              <div>
                <button
                  className={`btn btn-outline ${style.utilBtn} me-3`}
                  disabled={props.passwordListCalling}
                  onClick={() => handleDownloadButtonClick(pdfType.TEACHER)}
                >
                  仮パスワードダウンロード({pdfType.TEACHER})
                </button>
                <button
                  className={`btn btn-outline ${style.utilBtn}`}
                  disabled={props.passwordListCalling}
                  onClick={() => handleDownloadButtonClick(pdfType.STUDENT)}
                >
                  仮パスワードダウンロード({pdfType.STUDENT})
                </button>
              </div>
              {hasStudentWithoutClass ? (
                <div className={`${style.danger}`}>
                  <ErrorIcon />
                  クラス未設定の生徒がいます
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
      {(props.school.total_dlp_registrable_num! > 0 ||
        props.school.total_g4s_registrable_num! > 0) && (
        <div className={style.capacitySection}>
          {props.school.total_dlp_registrable_num! > 0 && (
            <CapacityRow
              label="教材利用可能人数"
              limitsByGrade={props.school.dlp_order_nums_by_grade}
              countsByGrade={studentCounts.byGrade}
              countKey="dlp"
              totalCount={studentCounts.totalDlp}
              totalLimit={props.school.total_dlp_registrable_num!}
              testAccountNum={props.school.dlp_test_account_num}
              maxGrade={maxGrade}
            />
          )}
          {props.school.total_g4s_registrable_num! > 0 && (
            <CapacityRow
              label="GEAR利用可能人数"
              limitsByGrade={props.school.g4s_order_nums_by_grade}
              countsByGrade={studentCounts.byGrade}
              countKey="g4s"
              totalCount={studentCounts.totalG4s}
              totalLimit={props.school.total_g4s_registrable_num!}
              testAccountNum={props.school.g4s_test_account_num}
              maxGrade={maxGrade}
            />
          )}
          <div className={style.capacityNote}>
            ※納品指定日後に人数は反映されます
          </div>
          {(props.school.dlp_test_account_num > 0 ||
            props.school.g4s_test_account_num > 0) && (
            <div className={style.capacityNote}>
              ※各学年の人数超過分はテスト枠で補われます
            </div>
          )}
        </div>
      )}
      <div className={style.control}>
        <div className={style.searchArea}>
          <label className="me-2">名前検索</label>
          <input
            value={searchName}
            placeholder="氏名を入力"
            onChange={e => {
              setSearchName(e.target.value);
            }}
          />
          <label className="ms-2 me-2">クラス検索</label>
          <select
            name="class"
            className="form-select"
            aria-label="select"
            value={searchClass}
            onChange={e => setSearchClass(Number(e.target.value))}
          >
            <option hidden>クラスを選択</option>
            <option value={0}>すべてのクラス</option>
            {[...props.classes, ...(props.next_classes || [])]?.map(c => {
              return (
                <option key={c.id} value={c.id}>
                  {c.full_name} ({c.year})
                </option>
              );
            })}
          </select>
        </div>
        <div className={style.filterArea}>
          <label className="ms-2 me-2">並び順</label>
          <select
            name="sortBy"
            className="form-select"
            aria-label="select"
            value={sortBy}
            onChange={e => setSortBy(e.target.value as EStudentSortType)}
          >
            {Object.values(EStudentSortType).map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className={style.tableWrapper}>
        <table className="table table-striped table-hover">
          <thead>
            <tr>
              <th>ID</th>
              <th>ログインID</th>
              <th>学籍番号</th>
              <th>名前</th>
              {props.isNextYear ? (
                <>
                  <th>
                    {currentYearShortLabel}年度
                    <br />
                    クラス
                  </th>
                  <th>
                    {nextYearShortLabel}年度
                    <br />
                    クラス
                  </th>
                  <th>
                    {nextYearShortLabel}年度
                    <br />
                    学年
                  </th>
                </>
              ) : (
                <>
                  <th>クラス</th>
                  <th>次の学年</th>
                </>
              )}
              <th>教材対象</th>
              <th>GEAR拡張</th>
              {isCramSchool && (
                <>
                  <th>プラン</th>
                  <th />
                </>
              )}
              <th>仮パスワード</th>
              <th />
              <th />
              {props.isMasqueradeByAdmin && <th />}
              <th className="text-center">最終アクセス日</th>
            </tr>
          </thead>
          <tbody>
            {students && students.length > 0 ? (
              students.map(s => {
                return (
                  <tr key={s.id}>
                    <td>
                      <span>{s.id}</span>
                    </td>
                    <td>{s.identity}</td>
                    <td>{s.student_no_in_school}</td>
                    <td className="font-weight-bold">
                      {s.last_name + " " + s.first_name}
                    </td>
                    <td>
                      {props.classes
                        .filter(c => s.class_id === c.id)
                        .map(c => (
                          <div key={`${c.id}`}>{c.full_name}</div>
                        ))}
                    </td>
                    {props.isNextYear && props.next_classes && (
                      <td>
                        {props.next_classes
                          .filter(nC => s.next_klass_id === nC.id)
                          .map(nC => (
                            <div key={`${nC.id}`}>{nC.full_name}</div>
                          ))}
                      </td>
                    )}
                    <td>{s.next_grade}</td>
                    <td>
                      {!(props.school.total_dlp_registrable_num! > 0)
                        ? "-"
                        : s.dlp_enabled
                        ? "◯"
                        : "×"}
                    </td>
                    <td>
                      {!(props.school.total_g4s_registrable_num! > 0)
                        ? "-"
                        : s.g4s_enabled
                        ? "◯"
                        : "×"}
                    </td>
                    {isCramSchool && (
                      <>
                        <td>
                          {s.plan_disabled
                            ? "終了"
                            : s.active_plans
                            ? s.active_plans.map(plan => plan.name).join(", ")
                            : ""}
                        </td>
                        <td className={style.dangerBtn}>
                          {!s.plan_disabled ? (
                            <button
                              className={`btn btn-outline}`}
                              onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                let selectedDate = new Date()
                                  .toISOString()
                                  .slice(0, 10);
                                ctx.actions.showModal(
                                  <Confirm
                                    content={
                                      <>
                                        <p>
                                          「{s.last_name} {s.first_name}
                                          」さんのプランを終了します。
                                          この操作を行うと、終了日からすべてのプランが終了となります。
                                          この操作は取り消すことができませんのでご注意ください。
                                        </p>
                                        <div style={{ marginTop: 16 }}>
                                          <label>
                                            終了日を選択してください：
                                            <input
                                              type="date"
                                              defaultValue={selectedDate}
                                              onChange={e => {
                                                selectedDate = e.target.value;
                                              }}
                                              style={{ marginLeft: 8 }}
                                            />
                                          </label>
                                        </div>
                                      </>
                                    }
                                    closeModal={() => ctx.actions.closeModal()}
                                    callback={() => {
                                      props.onSaveDisableStudentPlans(
                                        s.id,
                                        selectedDate,
                                      );
                                      ctx.actions.closeModal();
                                    }}
                                  />,
                                );
                              }}
                            >
                              退塾する
                            </button>
                          ) : (
                            <span>退塾処理済み</span>
                          )}
                        </td>
                      </>
                    )}
                    <td>
                      {s.temporary_key ? (
                        s.temporary_key
                      ) : (
                        <button
                          className={clsx(
                            "btn btn-outline-primary",
                            style.passwordResetButton,
                          )}
                          disabled={props.resetPasswordCalling}
                          onClick={e => {
                            ctx.actions.showModal(
                              <Confirm
                                content={
                                  <>
                                    <p>
                                      {s.last_name} {s.first_name}さんの
                                      <br />
                                      パスワードをリセットしてもよろしいですか?
                                    </p>
                                    <p>
                                      新しい仮パスワードを発行します。
                                      <br />
                                      生徒に再度仮パスワードをお伝え下さい。
                                    </p>
                                  </>
                                }
                                closeModal={() => {
                                  ctx.actions.closeModal();
                                }}
                                callback={() => {
                                  props.resetPassword(s.id);
                                  ctx.actions.closeModal();
                                }}
                              />,
                            );
                          }}
                        >
                          パスワードリセット
                        </button>
                      )}
                    </td>
                    <td className={style.utilBtnArea}>
                      <button
                        className="btn btn-outline"
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          props.toBuildStudent(s.id);
                        }}
                      >
                        編集
                      </button>
                    </td>
                    <td>
                      <div className={style.utilBtnArea}>
                        <button
                          className="btn btn-outline"
                          disabled={props.loginCalling}
                          onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            props.dlpLogin!(s.id);
                          }}
                        >
                          教材代理ログイン
                        </button>
                      </div>
                    </td>
                    {props.isMasqueradeByAdmin && (
                      <td>
                        <div className={style.utilBtnArea}>
                          <button
                            className="btn btn-outline"
                            disabled={props.loginCalling}
                            onClick={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              props.gearLogin!(s.id);
                            }}
                          >
                            GEAR代理ログイン
                          </button>
                        </div>
                      </td>
                    )}
                    <td className="text-center">
                      {s.latest_active_date ?? "ー"}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={props.isNextYear ? 11 : 10}>
                  該当する生徒が見つかりませんでした。
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StudentList;
